import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export type TextLevel = 'body' | 'compact' | 'small' | 'caption' | 'micro';
/* Headings (h1–h6) are deliberately excluded — use <Title> for those.
   Hard contract per spec §6.13. */
export type TextAs = 'p' | 'span' | 'div' | 'em' | 'strong';

const LEVEL_CLASS: Record<TextLevel, string> = {
  body: 'j-body',
  compact: 'j-compact-body',
  small: 'j-small-body',
  caption: 'j-caption',
  micro: 'j-micro',
};

/* Bounded rest-prop allow-list typed via React.HTMLAttributes
   (SectionHeader r1 lesson — avoids unknown-type collisions). */
type AllowedRest = Pick<React.HTMLAttributes<HTMLElement>, 'id' | 'title' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface TextProps extends StyledProps, WithChildren, UnsafeStyleProps, AllowedRest {
  level?: TextLevel;
  as?: TextAs;
  /* `default` uses --fg-1 (full-contrast body); `muted` uses --fg-3
     (secondary text). Tone is applied via inline style to override
     the typography.css color cascade. */
  tone?: 'default' | 'muted';
}

export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  { children, level = 'body', as = 'p', tone = 'default', className, unsafe_style, ...rest },
  ref,
) {
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLElement>;

  /* Tone is applied inline:
     - `muted` always forces --fg-3.
     - `default` defers to typography.css's per-level color (most
       levels inherit --fg-1 from the shared body rule; `.j-caption`
       is intentionally --fg-2 per the kit typography spec).
     The spec previously claimed default tone always = --fg-1; that
     was inconsistent with `.j-caption` and is now documented in
     spec §7 per codex r1.

     For `as="strong"`, force font-weight: 700 since `.j-body` (and
     siblings) set font-weight: 400 and would otherwise suppress
     the browser's default bold styling for the strong element. */
  const mutedStyle = tone === 'muted' ? { color: 'var(--fg-3)' } : undefined;
  const strongStyle = as === 'strong' ? { fontWeight: 700 as const } : undefined;
  const mergedStyle =
    mutedStyle || strongStyle || unsafe_style
      ? { ...mutedStyle, ...strongStyle, ...unsafe_style }
      : undefined;

  return React.createElement(
    as,
    {
      ref,
      ...restProps,
      className: cx(LEVEL_CLASS[level], className),
      style: mergedStyle,
    },
    children,
  );
});
